import React, { useState, useEffect } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { Button } from "@mui/material";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import {
  TrashIcon,
  PencilIcon,
  PlusIcon,
  ChartBarIcon,
  ArrowDownTrayIcon,
  DocumentArrowDownIcon,
} from "@heroicons/react/24/outline";
import ProConfirmation from "../../../utils/ProConfirmation";
import { TWclassNames } from "../../../utils/Div";
import { contractLinesApi } from "../../../../api/contract_lines";
import { axiosWithToast } from "../../../Utils";
import { ContractLineDialog } from "./contract-line-dialog";
import { ContractLineGroupDialog } from "./contract-line-group-dialog";
import { DeliveryProgressDialog } from "./delivery-progress-dialog";
import { ContractLinesExportDialog } from "./contract-lines-export-dialog";

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result.map((item, index) => ({
    ...item,
    row_order: index,
  }));
};

const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: "none",
  padding: 0,
  margin: `0 0 8px 0`,
  background: isDragging ? "lightgrey" : null,
  ...draggableStyle,
});

const MemoizedGroupRow = React.memo(({ item, provided, snapshot, onEdit, onDelete, canEdit = true }) => {
  return (
    <TableRow
      className="bg-gray-100"
      hover
      key={item.id}
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...(canEdit ? provided.dragHandleProps : {})}
      style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
    >
      <TableCell>
        {canEdit ? <DragHandleIcon /> : null}
      </TableCell>
      <TableCell align="left" colSpan={6}>
        <div className="font-semibold text-gray-900">{item.title || "Gruppe"}</div>
        {item.description && (
          <div className="text-sm text-gray-600 mt-1">{item.description}</div>
        )}
      </TableCell>
      <TableCell align="right">
        {canEdit ? (
        <div className="flex items-center justify-end space-x-2">
          <button
            onClick={() => onEdit(item)}
            className="text-blue-600 hover:text-blue-900"
            title="Rediger gruppe"
          >
            <PencilIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => onDelete(item)}
            className="text-red-600 hover:text-red-900"
            title="Slett gruppe"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </TableCell>
    </TableRow>
  );
});
MemoizedGroupRow.displayName = "MemoizedGroupRow";

const MemoizedLineRow = React.memo(
  ({
    item,
    provided,
    snapshot,
    onEdit,
    onDelete,
    onRegisterProgress,
    formatter,
    canEdit = true,
  }) => {
    const totalContractPrice = item.total_contract_price || 0;
    const totalCompletedPrice = item.total_completed_price || 0;
    // Calculate progress percentage safely
    const progressPercentage = (() => {
      if (item.progress_percentage !== null && item.progress_percentage !== undefined) {
        const value = parseFloat(item.progress_percentage);
        return isNaN(value) ? 0 : value;
      }
      // Calculate from contract_quantity and completed_quantity if progress_percentage not available
      const contractQty = parseFloat(item.contract_quantity) || 0;
      const completedQty = parseFloat(item.completed_quantity) || 0;
      if (contractQty > 0) {
        return (completedQty / contractQty) * 100;
      }
      return 0;
    })();

    return (
      <TableRow
        hover
        key={item.id}
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...(canEdit ? provided.dragHandleProps : {})}
        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
      >
        <TableCell>
          {canEdit ? <DragHandleIcon /> : null}
        </TableCell>
        <TableCell align="left">
          <div className="font-medium text-gray-900">
            {item.product?.displayTitle || item.title || item.description || "Ingen tittel"}
          </div>
          {item.description && item.product && (
            <div className="text-sm text-gray-600 mt-1">{item.description}</div>
          )}
        </TableCell>
        <TableCell align="center">
          <div className="text-gray-900">
            {item.contract_quantity || 0} {item.unit?.title || ""}
          </div>
        </TableCell>
        <TableCell align="center">
          <div className={TWclassNames(
            item.completed_quantity > 0 ? "text-green-600 font-semibold" : "text-gray-600"
          )}>
            {item.completed_quantity || 0} {item.unit?.title || ""}
          </div>
          {item.contract_quantity > 0 && (
            <div className="text-xs text-gray-500 mt-1">
              {typeof progressPercentage === 'number' ? progressPercentage.toFixed(1) : '0.0'}%
            </div>
          )}
        </TableCell>
        <TableCell align="center">
          <div className="text-sm text-gray-600">{item.unit?.title || "-"}</div>
        </TableCell>
        <TableCell align="right">
          <div className="text-gray-900">{formatter.format(item.unit_price || 0)}</div>
        </TableCell>
        <TableCell align="right">
          <div className="text-gray-900 font-medium">
            {formatter.format(totalContractPrice)}
          </div>
          {item.completed_quantity > 0 && (
            <div className="text-sm text-blue-600">
              Utført: {formatter.format(totalCompletedPrice)}
            </div>
          )}
        </TableCell>
        <TableCell align="right">
          {canEdit ? (
          <div className="flex items-center justify-end space-x-2">
            <button
              onClick={() => onRegisterProgress(item)}
              className="text-green-600 hover:text-green-900"
              title="Registrer fremdrift"
            >
              <ChartBarIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => onEdit(item)}
              className="text-blue-600 hover:text-blue-900"
              title="Rediger linje"
            >
              <PencilIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => onDelete(item)}
              className="text-red-600 hover:text-red-900"
              title="Slett linje"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
          ) : (
            <span className="text-xs text-gray-400">—</span>
          )}
        </TableCell>
      </TableRow>
    );
  }
);
MemoizedLineRow.displayName = "MemoizedLineRow";

export const ContractLines = ({ project, onRefresh, canEdit = true }) => {
  const [contractLines, setContractLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [selectedLine, setSelectedLine] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const formatter = new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency: "NOK",
  });

  useEffect(() => {
    fetchContractLines();
  }, [project?.id]);

  const fetchContractLines = async () => {
    if (!project?.id) return;
    
    setLoading(true);
    try {
      const response = await contractLinesApi.getContractLines(project.id);
      if (response.data?.success) {
        setContractLines(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching contract lines:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditLine = (line) => {
    setSelectedLine(line);
    setEditDialogOpen(true);
  };

  const handleEditGroup = (group) => {
    setSelectedGroup(group);
    setGroupDialogOpen(true);
  };

  const handleRegisterProgress = (line) => {
    setSelectedLine(line);
    setProgressDialogOpen(true);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setDeleteConfirmationOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete || !project?.id) return;

    await axiosWithToast(
      contractLinesApi.deleteContractLine(project.id, itemToDelete.id),
      () => {
        fetchContractLines();
      },
      () => {},
      () => {},
      "Sletter kontraktslinje..."
    );

    setDeleteConfirmationOpen(false);
    setItemToDelete(null);
  };

  const handleAddLine = () => {
    setSelectedLine(null);
    setEditDialogOpen(true);
  };

  const handleAddGroup = () => {
    setSelectedGroup(null);
    setGroupDialogOpen(true);
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setGroupDialogOpen(false);
    setProgressDialogOpen(false);
    setSelectedLine(null);
    setSelectedGroup(null);
  };

  const handleDialogSuccess = () => {
    fetchContractLines();
    handleDialogClose();
  };

  const handleExport = async (settings) => {
    setExporting(true);
    setExportDialogOpen(false);
    try {
      const response = await contractLinesApi.exportContractLines(project.id, settings);
      // Create blob and download
      const blob = new Blob([response.data], {
        type: settings.format === "excel" 
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf"
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `kontraktlinjer_${project.id}_${new Date().toISOString().split('T')[0]}.${settings.format === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting:", error);
    } finally {
      setExporting(false);
    }
  };

  const onDragEnd = async (result) => {
    if (!canEdit || !result.destination || !project?.id) return;

    const reorderedItems = reorder(
      contractLines,
      result.source.index,
      result.destination.index
    );

    setContractLines(reorderedItems);

    // Update row_order in API for all items
    try {
      const updatePromises = reorderedItems.map((item, index) => {
        if (item.group_row) {
          // For groups, we'd need a separate endpoint or handle differently
          // For now, skip groups
          return Promise.resolve();
        } else {
          return contractLinesApi.updateContractLine(project.id, item.id, {
            row_order: index,
          });
        }
      });

      await Promise.all(updatePromises);
    } catch (error) {
      console.error("Error updating row order:", error);
      // Revert on error
      fetchContractLines();
    }
  };

  // Expose totals calculation for parent component
  useEffect(() => {
    if (onRefresh && contractLines && contractLines.length > 0) {
      const totals = contractLines
        .filter((item) => !item.group_row)
        .reduce(
          (acc, item) => {
            const contractPrice = parseFloat(item.total_contract_price) || 0;
            const completedPrice = parseFloat(item.total_completed_price) || 0;
            acc.contractSum += isNaN(contractPrice) ? 0 : contractPrice;
            acc.completedSum += isNaN(completedPrice) ? 0 : completedPrice;
            return acc;
          },
          { contractSum: 0, completedSum: 0 }
        );

      totals.billableSum = totals.completedSum || 0;
      totals.remainingSum = (totals.contractSum || 0) - (totals.completedSum || 0);
      
      // Pass totals to parent via callback if needed
      if (onRefresh && typeof onRefresh === 'function') {
        onRefresh(totals);
      }
    } else if (onRefresh && (!contractLines || contractLines.length === 0)) {
      // If no contract lines, send empty totals
      onRefresh({
        contractSum: 0,
        completedSum: 0,
        billableSum: 0,
        remainingSum: 0
      });
    }
  }, [contractLines, onRefresh]);

  if (loading) {
    return <ResourceLoading />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          Kontraktslinjer
        </h3>
        <div className="flex space-x-2">
          <button
            onClick={() => setExportDialogOpen(true)}
            disabled={exporting}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                Laster...
              </>
            ) : (
              <>
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                Eksporter
              </>
            )}
          </button>
          {canEdit ? (
          <>
          <button
            onClick={handleAddGroup}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-600 hover:bg-gray-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Ny gruppe
          </button>
          <button
            onClick={handleAddLine}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Ny linje
          </button>
          </>
          ) : null}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 50 }}></TableCell>
              <TableCell>Beskrivelse</TableCell>
              <TableCell align="center">Kontrakt</TableCell>
              <TableCell align="center">Utført</TableCell>
              <TableCell align="center">Enhet</TableCell>
              <TableCell align="right">Enhetspris</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right" style={{ width: 150 }}>Handlinger</TableCell>
            </TableRow>
          </TableHead>

          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="contract-lines">
              {(provided) => (
                <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                  {contractLines.map((item, index) => (
                    <Draggable
                      key={item.id || `item-${index}`}
                      draggableId={String(item.id || `item-${index}`)}
                      index={index}
                      isDragDisabled={!canEdit}
                    >
                      {(provided, snapshot) =>
                        item.group_row ? (
                          <MemoizedGroupRow
                            item={item}
                            provided={provided}
                            snapshot={snapshot}
                            canEdit={canEdit}
                            onEdit={handleEditGroup}
                            onDelete={handleDelete}
                          />
                        ) : (
                          <MemoizedLineRow
                            item={item}
                            provided={provided}
                            snapshot={snapshot}
                            canEdit={canEdit}
                            onEdit={handleEditLine}
                            onDelete={handleDelete}
                            onRegisterProgress={handleRegisterProgress}
                            formatter={formatter}
                          />
                        )
                      }
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </DragDropContext>
        </Table>
      </div>

      {/* Dialogs */}
      <ContractLineDialog
        open={editDialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleDialogSuccess}
        project={project}
        contractLine={selectedLine}
      />

      <ContractLineGroupDialog
        open={groupDialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleDialogSuccess}
        project={project}
        group={selectedGroup}
      />

      <DeliveryProgressDialog
        open={progressDialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleDialogSuccess}
        project={project}
        contractLine={selectedLine}
      />

      <ContractLinesExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        onExport={handleExport}
      />

      <ProConfirmation
        severity="confirmation"
        open={deleteConfirmationOpen}
        message="Er du sikker på at du vil slette denne linjen/gruppen?"
        action="delete"
        module="contract_line"
        yesCallback={confirmDelete}
        cancelCallback={() => {
          setDeleteConfirmationOpen(false);
          setItemToDelete(null);
        }}
      />
    </div>
  );
};

